'use client';
import { useState, useEffect } from 'react';
import SignatureAkif from './SignatureAkif';

import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { href: '#home', label: 'Home' },
  { href: '#about', label: 'About' },
  { href: '#services', label: 'Services' },
  { href: '#portfolio', label: 'Portfolio' },
  { href: '#clients', label: 'Clients' },
  { href: '#contact', label: 'Contact' },
];

export default function NavBar({
  isDark,
  toggleTheme,
}: {
  isDark: boolean;
  toggleTheme: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      // Active section detection
      const sections = ['home', 'about', 'services', 'portfolio', 'clients', 'contact'];
      for (const sec of [...sections].reverse()) {
        const el = document.getElementById(sec);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(sec);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? 'var(--glass-bg)' : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--glass-border)' : 'none',
          padding: scrolled ? '12px 0' : '20px 0',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3">
            <SignatureAkif /> {/* directly use signature */}
            <div>
              <div
                className="text-xl font-bold leading-none pt-[8px]"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.03em' }}
              >
                Akif Khan
              </div>
              <div className="text-md font-mono" style={{ color: 'var(--text-muted)' }}>
                Full Stack Dev
              </div>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden tab:flex items-center gap-8">
            {navLinks.map(({ href, label }) => {
              const sec = href.replace('#', '');
              return (
                <a
                  key={href}
                  href={href}
                  className="nav-link"
                  style={{
                    color: activeSection === sec ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  {label}
                  {activeSection === sec && (
                    <span
                      className="absolute bottom-[-4px] left-0 w-full h-0.5 rounded-full"
                      style={{
                        background: 'linear-gradient(90deg, #6C63FF, #00D4FF)',
                      }}
                    />
                  )}
                </a>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Theme toggle */}
            <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
              <div className="theme-toggle-thumb flex items-center justify-center">
                {isDark ? (
                  <Icon name="MoonIcon" size={12} className="text-white" />
                ) : (
                  <Icon name="SunIcon" size={12} className="text-white" />
                )}
              </div>
            </button>

            {/* CTA */}
            <a href="#contact" className="hidden md:block">
              <button
                className="btn-primary relative z-10"
                style={{ padding: '10px 24px', fontSize: '0.85rem' }}
              >
                Let’s Build
              </button>
            </a>

            {/* Mobile hamburger */}
            <button
              className="tab:hidden w-10 h-10 flex items-center justify-center rounded-xl glass-card"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <Icon
                name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={20}
                style={{ color: 'var(--text-primary)' }}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className="fixed inset-0 z-40 tab:hidden overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16, 1, 0.3, 1)]"
        style={{
          background: mobileOpen ? 'var(--glass-bg)' : 'transparent',
          backdropFilter: mobileOpen ? 'blur(40px) saturate(150%)' : 'blur(0px)',
          visibility: mobileOpen ? 'visible' : 'hidden',
          opacity: mobileOpen ? 1 : 0,
        }}
      >
        {/* Animated Background Blob - Makes the glass effect look "alive" */}
        <div
          className="absolute -top-[10%] -right-[10%] w-64 h-64 rounded-full blur-[100px] opacity-20 animate-pulse"
          style={{
            background: 'linear-gradient(90deg, #6C63FF, #00D4FF)',
            display: mobileOpen ? 'block' : 'none',
          }}
        />

        <div className="relative flex flex-col h-full px-8 pt-28 pb-12 overflow-y-auto">
          {/* Section Header */}

          {/* Navigation Links */}
          <nav className="flex flex-col gap-1">
            {navLinks.map(({ href, label }, index) => {
              const sec = href.replace('#', '');
              return (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMobileOpen(false)}
                  className="group relative flex items-baseline gap-4 py-2 transition-all duration-500"
                  style={{
                    transitionDelay: `${index * 60 + 150}ms`,
                    transform: mobileOpen
                      ? 'translateX(0) skewX(0deg)'
                      : 'translateX(-40px) skewX(-10deg)',
                    opacity: mobileOpen ? 1 : 0,
                  }}
                >
                  {/* Numbering - Looks very high-end */}
                  <span className="text-xs font-mono opacity-30 group-hover:opacity-100 transition-opacity">
                    0{index + 1}
                  </span>

                  <span
                    className="text-3xl font-bold tracking-tighter transition-transform group-active:scale-95"
                    style={{
                      color:
                        activeSection === sec ? 'var(--text-primary)' : 'var(--text-secondary)',
                      WebkitTextStroke:
                        activeSection !== sec ? '1px rgba(255,255,255,0.1)' : 'none',
                    }}
                  >
                    {label}
                  </span>

                  {/* Hover/Active Dot */}
                  {activeSection === sec && (
                    <div className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-1.5 h-8 rounded-full bg-gradient-to-b from-[#6C63FF] to-[#00D4FF]" />
                  )}
                </a>
              );
            })}
          </nav>

          {/* Bottom Section - Socials/Contact */}
          <div
            className="mt-auto pt-4"
            style={{
              transform: mobileOpen ? 'translateY(0)' : 'translateY(40px)',
              opacity: mobileOpen ? 1 : 0,
              transition: 'all 0.6s 0.5s ease-out',
            }}
          >
            <div className="grid grid-cols-1 gap-4">
              <div className="p-6 rounded-3xl border border-[var(--glass-border)] bg-white/[0.03] backdrop-blur-sm">
                <p className="text-xs font-mono mb-2 opacity-50">Have an idea?</p>
                <a
                  href="https://wa.me/923343374837"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-md font-medium hover:underline decoration-[#25D366] underline-offset-4 flex items-center gap-2 group"
                >
                  Message me on WhatsApp
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="group-hover:translate-x-1 transition-transform"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <a href="#contact" onClick={() => setMobileOpen(false)} className="w-full">
                <button className="btn-primary w-full py-5 rounded-2xl text-lg font-bold shadow-2xl shadow-[#6C63FF]/20 flex items-center justify-center gap-3">
                  Start a Project
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="group-hover:translate-x-1 transition-transform"
                  >
                    <path
                      d="M4.16663 10H15.8333M15.8333 10L10.8333 5M15.8333 10L10.8333 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
